import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface HeartRateDataPoint {
  timestamp: number;
  bpm: number;
  waveValue: number;
}

export interface BreathRateDataPoint {
  timestamp: number;
  rate: number;
  waveValue: number;
}

export interface ObjectDetection {
  id: string;
  timestamp: number;
  objectClass: string;
  confidence: number;
  bbox: { x: number; y: number; width: number; height: number };
  metadata?: Record<string, unknown>;
}

export interface ThermalDataPoint {
  timestamp: number;
  maxTemp: number;
  minTemp: number;
  avgTemp: number;
  gridData?: number[];
}

export interface ImageData {
  id: string;
  sessionId?: string;
  deviceName?: string;
  timestamp: number;
  filename?: string;
  width?: number;
  height?: number;
  sizeBytes?: number;
  metadata?: Record<string, unknown>;
  image?: string;  // Base64 image data for live viewing without session
}

export interface DogPoseDataPoint {
  id?: number;
  sessionId?: string;
  deviceName?: string;
  timestamp: number;
  startedAt?: number;
  dogState?: string;
  bboxX?: number;
  bboxY?: number;
  bboxWidth?: number;
  bboxHeight?: number;
  bboxConfidence?: number;
  facingDirection?: string;
  facingAngle?: number;
  facingConfidence?: number;
  isValid?: boolean;
}

export interface DashboardSession {
  id: string;
  cameraName: string;
  cameraUrl: string;
  status: 'idle' | 'running' | 'paused' | 'stopped';
  startedAt: number | null;
  endedAt: number | null;
}

export interface DeviceData<T> {
  deviceName: string;
  lastUpdated: number;
  data: T[];
}

export interface DogPoseData {
  id: string;
  imageId?: string;
  sessionId?: string;
  deviceName: string;
  timestamp: number;
  bboxX: number;
  bboxY: number;
  bboxWidth: number;
  bboxHeight: number;
  bboxConfidence: number;
  headCenterX?: number;
  headCenterY?: number;
  tailBaseX?: number;
  tailBaseY?: number;
  facingDirection: string;
  facingAngle: number;
  facingAngleDegrees?: number;
  facingConfidence: number;
  keypoints: Array<{ name: string; x: number; y: number; confidence: number }>;
  isValid: boolean;
}

export interface PlaybackData {
  deviceName: string;
  issueType?: string;
  from?: number;
  to?: number;
  heartRate: Array<Record<string, unknown> & { timestamp: number; bpm: number; deviceName: string }>;
  breathRate: Array<Record<string, unknown> & { timestamp: number; rate: number; deviceName: string }>;
  thermal: Array<Record<string, unknown> & { timestamp: number; deviceName: string }>;
  dogPoses: DogPoseData[];
  images: ImageData[];
  eventFolders: string[];
}

export type DashboardViewMode = 'realtime' | 'playback';

export type DashboardPanelId = 'heartRate' | 'breathRate' | 'thermal' | 'petDetection' | 'images';

export const ALL_PANELS: DashboardPanelId[] = ['heartRate', 'breathRate', 'thermal', 'petDetection', 'images'];

export const PANEL_LABELS: Record<DashboardPanelId, string> = {
  heartRate: 'Heart Rate',
  breathRate: 'Breath Rate',
  thermal: 'Thermal',
  petDetection: 'Pet Detection',
  images: 'Images',
};

export interface DashboardState {
  isConnected: boolean;
  cameraName: string;
  cameraUrl: string;
  currentSession: DashboardSession | null;
  heartRateByDevice: Record<string, HeartRateDataPoint[]>;
  breathRateByDevice: Record<string, BreathRateDataPoint[]>;
  thermalByDevice: Record<string, ThermalDataPoint[]>;
  objectDetections: ObjectDetection[];
  images: ImageData[];
  dogPoses: DogPoseDataPoint[];
  dogPoseFrequency: Record<string, number>;
  dogPoseDuration: Record<string, number>;
  previousDogPoseFrequency: Record<string, number>;
  previousDogPoseDuration: Record<string, number>;
  distinctDogStates: string[];
  isSimulating: boolean;
  isSessionActive: boolean;
  viewMode: DashboardViewMode;
  selectedPet: string;
  selectedIssueType: string;
  selectedTimestamp: string;
  playbackData: PlaybackData | null;
  isPlaybackLoading: boolean;
  isPlaybackPlaying: boolean;
  playbackProgress: number;
  visiblePanels: DashboardPanelId[];
}

const RING_BUFFER_SIZE = 300;

const DEFAULT_VISIBLE_PANELS: DashboardPanelId[] = ['heartRate', 'breathRate', 'thermal', 'petDetection'];

function loadVisiblePanels(): DashboardPanelId[] {
  try {
    const saved = localStorage.getItem('dashboard_visible_panels');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch { /* ignore */ }
  return DEFAULT_VISIBLE_PANELS;
}

const initialState: DashboardState = {
  isConnected: false,
  cameraName: '',
  cameraUrl: '',
  currentSession: null,
  heartRateByDevice: {},
  breathRateByDevice: {},
  thermalByDevice: {},
  objectDetections: [],
  images: [],
  dogPoses: [],
  dogPoseFrequency: {},
  dogPoseDuration: {},
  previousDogPoseFrequency: {},
  previousDogPoseDuration: {},
  distinctDogStates: [],
  isSimulating: false,
  isSessionActive: false,
  viewMode: 'realtime',
  selectedPet: '',
  selectedIssueType: '',
  selectedTimestamp: '',
  playbackData: null,
  isPlaybackLoading: false,
  isPlaybackPlaying: false,
  playbackProgress: 0,
  visiblePanels: loadVisiblePanels(),
};

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setConnectionStatus(state, action: PayloadAction<{ isConnected: boolean; cameraName?: string; cameraUrl?: string }>) {
      state.isConnected = action.payload.isConnected;
      if (action.payload.cameraName !== undefined) {
        state.cameraName = action.payload.cameraName;
      }
      if (action.payload.cameraUrl !== undefined) {
        state.cameraUrl = action.payload.cameraUrl;
      }
    },

    setCurrentSession(state, action: PayloadAction<DashboardSession | null>) {
      state.currentSession = action.payload;
    },

    addHeartRateData(state, action: PayloadAction<HeartRateDataPoint & { deviceName: string }>) {
      const { deviceName, ...data } = action.payload;
      if (!state.heartRateByDevice[deviceName]) {
        state.heartRateByDevice[deviceName] = [];
      }
      state.heartRateByDevice[deviceName].push(data);
      if (state.heartRateByDevice[deviceName].length > RING_BUFFER_SIZE) {
        state.heartRateByDevice[deviceName].shift();
      }
    },

    addBreathRateData(state, action: PayloadAction<BreathRateDataPoint & { deviceName: string }>) {
      const { deviceName, ...data } = action.payload;
      if (!state.breathRateByDevice[deviceName]) {
        state.breathRateByDevice[deviceName] = [];
      }
      state.breathRateByDevice[deviceName].push(data);
      if (state.breathRateByDevice[deviceName].length > RING_BUFFER_SIZE) {
        state.breathRateByDevice[deviceName].shift();
      }
    },

    addObjectDetection(state, action: PayloadAction<ObjectDetection>) {
      state.objectDetections.push(action.payload);
      if (state.objectDetections.length > 100) {
        state.objectDetections.shift();
      }
    },

    addThermalData(state, action: PayloadAction<ThermalDataPoint & { deviceName: string }>) {
      const { deviceName, ...data } = action.payload;
      if (!state.thermalByDevice[deviceName]) {
        state.thermalByDevice[deviceName] = [];
      }
      state.thermalByDevice[deviceName].push(data);
      if (state.thermalByDevice[deviceName].length > RING_BUFFER_SIZE) {
        state.thermalByDevice[deviceName].shift();
      }
    },

    addImage(state, action: PayloadAction<ImageData>) {
      state.images.push(action.payload);
      if (state.images.length > 50) {
        state.images.shift();
      }
    },

    clearImages(state) {
      state.images = [];
    },

    clearHeartRateBuffer(state) {
      state.heartRateByDevice = {};
    },

    clearBreathRateBuffer(state) {
      state.breathRateByDevice = {};
    },

    clearThermalBuffer(state) {
      state.thermalByDevice = {};
    },

    clearObjectDetections(state) {
      state.objectDetections = [];
    },

    addDogPose(state, action: PayloadAction<DogPoseDataPoint>) {
      state.dogPoses.push(action.payload);
      if (state.dogPoses.length > 500) {
        state.dogPoses.shift();
      }
    },

    setDogPoses(state, action: PayloadAction<DogPoseDataPoint[]>) {
      state.dogPoses = action.payload;
    },

    setDogPoseFrequency(state, action: PayloadAction<Record<string, number>>) {
      state.dogPoseFrequency = action.payload;
    },

    setDogPoseDuration(state, action: PayloadAction<Record<string, number>>) {
      state.dogPoseDuration = action.payload;
    },

    setPreviousDogPoseFrequency(state, action: PayloadAction<Record<string, number>>) {
      state.previousDogPoseFrequency = action.payload;
    },

    setPreviousDogPoseDuration(state, action: PayloadAction<Record<string, number>>) {
      state.previousDogPoseDuration = action.payload;
    },

    setDistinctDogStates(state, action: PayloadAction<string[]>) {
      state.distinctDogStates = action.payload;
    },

    clearDogPoses(state) {
      state.dogPoses = [];
      state.dogPoseFrequency = {};
      state.dogPoseDuration = {};
      state.previousDogPoseFrequency = {};
      state.previousDogPoseDuration = {};
      state.distinctDogStates = [];
    },

    setSimulating(state, action: PayloadAction<boolean>) {
      state.isSimulating = action.payload;
    },

    resetDashboard(state) {
      state.isConnected = false;
      state.cameraName = '';
      state.cameraUrl = '';
      state.currentSession = null;
      state.heartRateByDevice = {};
      state.breathRateByDevice = {};
      state.thermalByDevice = {};
      state.objectDetections = [];
      state.dogPoses = [];
      state.images = [];
      state.dogPoses = [];
      state.dogPoseFrequency = {};
      state.dogPoseDuration = {};
      state.previousDogPoseFrequency = {};
      state.previousDogPoseDuration = {};
      state.distinctDogStates = [];
      state.isSimulating = false;
      state.isSessionActive = false;
    },

    setSessionActive(state, action: PayloadAction<boolean>) {
      state.isSessionActive = action.payload;
    },

    setViewMode(state, action: PayloadAction<DashboardViewMode>) {
      state.viewMode = action.payload;
    },

    setSelectedPet(state, action: PayloadAction<string>) {
      state.selectedPet = action.payload;
    },

    setSelectedIssueType(state, action: PayloadAction<string>) {
      state.selectedIssueType = action.payload;
    },

    setSelectedTimestamp(state, action: PayloadAction<string>) {
      state.selectedTimestamp = action.payload;
    },

    setPlaybackData(state, action: PayloadAction<PlaybackData | null>) {
      state.playbackData = action.payload;
      state.isPlaybackLoading = false;
    },

    setPlaybackLoading(state, action: PayloadAction<boolean>) {
      state.isPlaybackLoading = action.payload;
    },

    setPlaybackPlaying(state, action: PayloadAction<boolean>) {
      state.isPlaybackPlaying = action.payload;
    },

    setPlaybackProgress(state, action: PayloadAction<number>) {
      state.playbackProgress = action.payload;
    },

    togglePanel(state, action: PayloadAction<DashboardPanelId>) {
      const panel = action.payload;
      const idx = state.visiblePanels.indexOf(panel);
      if (idx >= 0) {
        state.visiblePanels.splice(idx, 1);
      } else {
        state.visiblePanels.push(panel);
      }
      try {
        localStorage.setItem('dashboard_visible_panels', JSON.stringify(state.visiblePanels));
      } catch { /* ignore */ }
    },

    setVisiblePanels(state, action: PayloadAction<DashboardPanelId[]>) {
      state.visiblePanels = action.payload;
      try {
        localStorage.setItem('dashboard_visible_panels', JSON.stringify(state.visiblePanels));
      } catch { /* ignore */ }
    },
  },
});

export const {
  setConnectionStatus,
  setCurrentSession,
  addHeartRateData,
  addBreathRateData,
  addObjectDetection,
  addThermalData,
  addImage,
  addDogPose,
  setDogPoses,
  setDogPoseFrequency,
  setDogPoseDuration,
  setPreviousDogPoseFrequency,
  setPreviousDogPoseDuration,
  setDistinctDogStates,
  clearHeartRateBuffer,
  clearBreathRateBuffer,
  clearObjectDetections,
  clearDogPoses,
  clearThermalBuffer,
  clearImages,
  setSimulating,
  resetDashboard,
  setSessionActive,
  setViewMode,
  setSelectedPet,
  setSelectedIssueType,
  setSelectedTimestamp,
  setPlaybackData,
  setPlaybackLoading,
  setPlaybackPlaying,
  setPlaybackProgress,
  togglePanel,
  setVisiblePanels,
} = dashboardSlice.actions;

export default dashboardSlice.reducer;